import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

import { PauseIcon, PlayArrowIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';

import { useCreateObjectUrl } from '../../../../../VideoUpload/components/VideoPlayer/hooks/useCreateObjectUrl';

type Props = {
	blob: Blob;
	takeLengthSec: number | undefined;
};

/**
 * The recorded take, played back where the camera preview was.
 *
 * Not the preview screen's `VideoPlayer`: that one wears the browser's native controls, which would put
 * a chrome bar across a surface this arm keeps clean, and it is muted - fine for a silent check of the
 * framing, useless for hearing what you said. Here the whole surface is the play control.
 */
export const TakePlayback: React.FC<Props> = ({ blob, takeLengthSec }) => {
	const { createObjectURL } = useCreateObjectUrl();
	const { translate } = useI18n();
	const videoRef = useRef<HTMLVideoElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) {
			return () => {};
		}

		const url = createObjectURL(blob);
		video.src = url;
		setIsPlaying(false);

		// Released when the take is replaced. This screen stays mounted across retries, so without this
		// every re-record would strand another blob URL for the life of the page.
		return () => {
			window.URL.revokeObjectURL(url);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blob]);

	const togglePlayback = useCallback(() => {
		const video = videoRef.current;
		if (!video) {
			return;
		}

		if (isPlaying) {
			video.pause();
			return;
		}

		const play = async () => {
			try {
				await video.play();
			} catch {
				// A rejected play leaves `isPlaying` false, which is what the element's own events would have
				// said anyway. Nothing to recover from and nothing worth reporting: the control is still there.
			}
		};

		void play();
	}, [isPlaying]);

	return (
		<>
			<video
				ref={videoRef}
				playsInline
				className="absolute inset-0 h-full w-full object-cover"
				data-testid="consolidated-take-element"
				onPlay={() => setIsPlaying(true)}
				onPause={() => setIsPlaying(false)}
				onEnded={() => {
					// Rewound so the surface at rest always shows the same frame, rather than whichever one the
					// take happened to end on.
					if (videoRef.current) {
						videoRef.current.currentTime = 0;
					}
					setIsPlaying(false);
				}}
			/>

			<button
				type="button"
				onClick={togglePlayback}
				// Keys stay literal at the call site so `no-invalid-translation-keys` can still check them.
				aria-label={
					isPlaying
						? translate('corgi.petition_actions.video_first_consolidated_ask.pause_take')
						: translate('corgi.petition_actions.video_first_consolidated_ask.play_take')
				}
				data-testid="consolidated-take-toggle"
				className={cn(
					'focus-visible:outline-stroke-strong absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3 border-0 transition-colors duration-200 focus-visible:outline-2 focus-visible:-outline-offset-2 motion-reduce:transition-none',
					isPlaying ? 'bg-transparent' : 'bg-black/55',
				)}
			>
				{/*
				 * Stays on screen while playing, at a lower opacity. This is a touch surface, so a control
				 * revealed on hover would be unreachable.
				 */}
				<span
					className={cn(
						'flex h-16 w-16 items-center justify-center rounded-full border-2 border-white transition-opacity duration-200 motion-reduce:transition-none',
						isPlaying ? 'opacity-75' : 'opacity-100',
					)}
				>
					{isPlaying ? (
						<PauseIcon size={36} className="text-white" />
					) : (
						<PlayArrowIcon size={36} className="text-white" />
					)}
				</span>

				{!isPlaying && takeLengthSec !== undefined && (
					<span className="text-[13px] font-medium text-white/80">
						<TranslatePlural
							value="corgi.petition_actions.video_first_consolidated_ask.take_length"
							count={takeLengthSec}
							replacements={{ seconds: takeLengthSec }}
						/>
					</span>
				)}
			</button>
		</>
	);
};
